import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { useApi } from "../utils/Api";
import OrderHistory from "../components/OrderHistory";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, setUser } = useUser();
  const { apiFetch } = useApi();

  const [activeTab, setActiveTab] = useState("profile");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) {
      navigate("/", { replace: true });
      return;
    }

    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setEmail(user?.email || "");
    setPhone(user?.phone || "");
  }, [user, navigate]);

  const handleSave = async () => {
    if (!user?.id) return;

    const fn = firstName.trim();
    const ln = lastName.trim();
    const em = email.trim();
    const ph = String(phone ?? "").trim();

    if (!fn || !ln || !em || !ph) {
      setError("All fields are required.");
      return;
    }

    const emailOk = /^\S+@\S+\.\S+$/.test(em);
    if (!emailOk) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await apiFetch("update-profile", {
        user_id: user.id,
        first_name: fn,
        last_name: ln,
        phone: ph,
        email: em,
      });

      if (data.status === "success") {
        const updatedUser = {
          ...user,
          first_name: fn,
          last_name: ln,
          phone: ph,
          email: em,
        };

        setUser?.(updatedUser);
      } else {
        if (data.message?.includes("Phone")) {
          setError("Phone number is already in use.");
        } else if (data.message?.includes("Email")) {
          setError("Email is already in use.");
        } else {
          setError(data.message || "Failed to update profile.");
        }
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout?.();
    navigate("/", { replace: true });
  };

  if (!user) return null;

  return (
    <main className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-[1200px] px-4 pb-10 pt-[120px] sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Profile</h1>
            <p className="mt-2 text-white/70">
              Manage your details and view your order history.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/10 px-5 py-3 font-semibold text-white transition hover:border-[#fa9324] hover:bg-[#fa9324] hover:text-black"
          >
            Back
          </button>
        </div>

        <div className="rounded-[28px] border border-white/10 bg-white p-6 text-black shadow-[0_18px_45px_rgba(0,0,0,0.22)] sm:p-8">
          <div className="mb-8 flex flex-wrap gap-3">
            <button
              type="button"
              className={`rounded-full px-5 py-3 font-semibold transition ${
                activeTab === "profile"
                  ? "bg-[#fa9324] text-black"
                  : "bg-black/5 text-black hover:bg-black/10"
              }`}
              onClick={() => setActiveTab("profile")}
            >
              Profile
            </button>

            <button
              type="button"
              className={`rounded-full px-5 py-3 font-semibold transition ${
                activeTab === "orders"
                  ? "bg-[#fa9324] text-black"
                  : "bg-black/5 text-black hover:bg-black/10"
              }`}
              onClick={() => setActiveTab("orders")}
            >
              Order History
            </button>
          </div>

          {activeTab === "profile" && (
            <div className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-black">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full rounded-2xl border border-black/20 bg-white px-4 py-3.5 text-sm text-black outline-none transition focus:border-[#fa9324] focus:ring-4 focus:ring-[#fa9324]/20"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full rounded-2xl border border-black/20 bg-white px-4 py-3.5 text-sm text-black outline-none transition focus:border-[#fa9324] focus:ring-4 focus:ring-[#fa9324]/20"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black">
                  Phone
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-2xl border border-black/20 bg-white px-4 py-3.5 text-sm text-black outline-none transition focus:border-[#fa9324] focus:ring-4 focus:ring-[#fa9324]/20"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-2xl border border-black/20 bg-white px-4 py-3.5 text-sm text-black outline-none transition focus:border-[#fa9324] focus:ring-4 focus:ring-[#fa9324]/20"
                />
              </div>

              {error && <p className="text-sm font-medium text-red-600">{error}</p>}

              <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="inline-flex items-center justify-center rounded-full bg-[#fa9324] px-6 py-3.5 font-bold text-black transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Save"}
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex items-center justify-center rounded-full bg-black/5 px-6 py-3.5 font-semibold text-black transition hover:bg-black/10"
                >
                  Logout
                </button>
              </div>
            </div>
          )}

          {activeTab === "orders" && <OrderHistory user={user} />}
        </div>
      </div>
    </main>
  );
}